import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { EntityType } from '../types/softDelete.types';

const ENTITY_LABELS: Record<EntityType, string> = {
  contact:            'contact',
  client:             'client',
  meeting:            'meeting',
  file_note:          'file note',
  agreement:          'agreement',
  fact_find:          'fact find',
  note:               'note',
  file_upload:        'file upload',
  signature_envelope: 'signature envelope',
  drive_file:         'file',
};

interface Props {
  open: boolean;
  entityType?: EntityType;
  entityName: string;
  description?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  /** When true, Cancel/Delete are disabled and Delete shows a spinner. */
  isProcessing?: boolean;
  /** Label on the confirm button when not processing (default: Delete). */
  confirmLabel?: string;
}

export default function ConfirmDeleteModal({
  open,
  entityType,
  entityName,
  description,
  onConfirm,
  onCancel,
  isProcessing = false,
  confirmLabel = 'Delete',
}: Props) {
  const defaultDescription = entityType ? (
    <>
      Do you really want to delete <strong>{entityName}</strong>?
    </>
  ) : (
    <>
      Do you really want to delete <strong>{entityName}</strong>? 
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isProcessing) onCancel();
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>
        <WarningIcon color="error" sx={{ fontSize: 40 }} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="h6" align="center">
          Are you sure?
        </Typography>
        <Typography align="center" color="text.secondary">
          {description ?? defaultDescription}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
        <Button onClick={onCancel} variant="outlined" disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={isProcessing}
          startIcon={
            isProcessing ? (
              <CircularProgress size={18} sx={{ color: 'common.white' }} />
            ) : undefined
          }
        >
          {isProcessing ? 'Deleting...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
